import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import BASE_URL from '../../config';
import '../userDash.css';

const STATUS_LABELS = {
  ordered: 'Bestellt',
  ready: 'Bereit zur Lieferung',
  delivered: 'Geliefert',
  refunded: 'Erstattet',
  canceled: 'Storniert',
};

const UserDash = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reordering, setReordering] = useState(null);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const fetchOrders = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Bitte melden Sie sich an, um Ihre Bestellhistorie zu sehen.');
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${BASE_URL}/api/orderhistory/`, {
          headers: { Authorization: `Token ${token}` },
        });
        setOrders(res.data);
      } catch (err) {
        console.error('Bestellhistorie konnte nicht geladen werden:', err);
        setError('Bestellhistorie konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const handleReorder = async (order) => {
    const token = localStorage.getItem('authToken');
    if (!token) { navigate('/login'); return; }

    setReordering(order.order_id);
    let added = 0;
    const failed = [];

    for (const item of order.items) {
      try {
        await axios.post(
          `${BASE_URL}/api/add-to-cart/`,
          { slug: item.item_slug, quantity: item.quantity },
          { headers: { Authorization: `Token ${token}` } }
        );
        added++;
      } catch (err) {
        console.warn(`Konnte "${item.item_title}" nicht hinzufügen:`, err?.response?.data?.error);
        failed.push(item.item_title);
      }
    }

    setReordering(null);

    if (failed.length > 0 && added === 0) {
      setToast({ type: 'warning', text: 'Keiner der Artikel konnte hinzugefügt werden. Sie sind möglicherweise nicht mehr verfügbar.' });
    } else if (failed.length > 0) {
      setToast({ type: 'warning', text: `${added} Artikel hinzugefügt. Nicht verfügbar: ${failed.join(', ')}.` });
      navigate('/cart');
    } else {
      setToast({ type: 'success', text: 'Alle Artikel wurden in den Warenkorb gelegt!' });
      navigate('/cart');
    }
  };

  if (loading) {
    return (
      <div className="oh-page">
        <h1 className="oh-title">Meine Bestellungen</h1>
        <p className="oh-loading">Bestellungen werden geladen…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="oh-page">
        <h1 className="oh-title">Meine Bestellungen</h1>
        <p className="oh-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="oh-page">
      <h1 className="oh-title">Meine Bestellungen</h1>

      {/* ── Toast notification ── */}
      {toast && (
        <div className={`oh-toast oh-toast--${toast.type}`}>
          <span>{toast.text}</span>
          <button className="oh-toast-close" onClick={() => setToast(null)}>✕</button>
        </div>
      )}

      {orders.length === 0 ? (
        <div className="oh-empty">
          <p>Du hast noch keine Bestellungen aufgegeben.</p>
          <button className="oh-shop-btn" onClick={() => navigate('/products')}>
            Jetzt einkaufen
          </button>
        </div>
      ) : (
        <div className="oh-list">
          {orders.map((order) => (
            <div key={order.order_id} className="oh-card">
              {/* ── Header row ── */}
              <div className="oh-card-header">
                <div className="oh-card-meta">
                  <span className="oh-order-id">
                    Bestellung {order.order_number} vom {new Date(order.created_at).toLocaleDateString('de-CH')}
                  </span>
                </div>
                <span className={`oh-status oh-status--${order.order_status}`}>
                  {STATUS_LABELS[order.order_status] || order.order_status}
                </span>
              </div>

              {/* ── Delivery info ── */}
              {order.delivery_date && (
                <div className="oh-delivery">
                  Lieferung: {new Date(order.delivery_date).toLocaleDateString('de-CH', {
                    year: 'numeric', month: 'short', day: 'numeric',
                  })}
                  {order.delivery_time && ` · ${order.delivery_time}`}
                </div>
              )}

              {/* ── Items table ── */}
              <table className="oh-items">
                <thead>
                  <tr>
                    <th className="oh-th-product">Produkt</th>
                    <th className="oh-th-qty">Anz.</th>
                    <th className="oh-th-price">Preis</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.item_title}</td>
                      <td className="oh-center">{item.quantity}</td>
                      <td className="oh-right">CHF {parseFloat(item.price).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* ── Footer row ── */}
              <div className="oh-card-footer">
                <div className="oh-total">
                  Total: <strong>CHF {parseFloat(order.total).toFixed(2)}</strong>
                </div>
                <button
                  className="oh-reorder-btn"
                  onClick={() => handleReorder(order)}
                  disabled={reordering === order.order_id}
                >
                  {reordering === order.order_id ? 'Wird hinzugefügt…' : '🔄 Nachbestellen'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserDash;
